import React, { useMemo } from 'react'
import Pagination from '../Pagination/Pagination.component'
import { DEFAULT_PAGINATION_PAGE_SIZE_OPTIONS } from '../Pagination/Pagination.utils'
import type { WorkScheduleProps } from './WorkSchedule.types'
import './WorkSchedule.css'

const WorkSchedule: React.FC<WorkScheduleProps> = ({
  dayHeaders,
  staff,
  loading = false,
  emptyMessage = 'No staff found.',
  staffColumnLabel = 'Staff members',
  darkMode = false,
  className = '',
  ariaLabel = 'Staff work schedule',
  pagination,
}) => {
  const totalPages = useMemo(() => {
    if (!pagination) return 1
    return Math.max(1, Math.ceil(pagination.totalRecords / pagination.pageSize))
  }, [pagination])

  const showPagination =
    Boolean(pagination) && !loading && pagination!.totalRecords > 0

  return (
    <div
      className={`ds-work-schedule${darkMode ? ' ds-work-schedule--dark' : ''}${
        className ? ` ${className}` : ''
      }`.trim()}
    >
      <div className="ds-work-schedule__table-block">
        <div
          className="ds-work-schedule__table"
          role="table"
          aria-label={ariaLabel}
          aria-busy={loading || undefined}
        >
          <div
            className="ds-work-schedule__row ds-work-schedule__row--header"
            role="row"
          >
            <div
              className="ds-work-schedule__cell ds-work-schedule__cell--staff ds-work-schedule__cell--staff-header"
              role="columnheader"
            >
              <span className="ds-work-schedule__text-bold">
                {staffColumnLabel}
              </span>
            </div>
            {dayHeaders.map((day) => (
              <div
                key={`${day.name}-${day.date}`}
                className="ds-work-schedule__cell ds-work-schedule__cell--day-header"
                role="columnheader"
              >
                <span className="ds-work-schedule__text-bold">{day.name}</span>
                <span className="ds-work-schedule__text-muted">{day.date}</span>
              </div>
            ))}
          </div>

          {!loading && staff.length === 0 && (
            <div className="ds-work-schedule__row" role="row">
              <div
                className="ds-work-schedule__cell ds-work-schedule__cell--empty"
                role="cell"
                style={{ gridColumn: '1 / -1' }}
              >
                <span className="ds-work-schedule__text-muted">
                  {emptyMessage}
                </span>
              </div>
            </div>
          )}

          {!loading &&
            staff.map((row) => (
              <div key={row.id} className="ds-work-schedule__row" role="row">
                <div
                  className="ds-work-schedule__cell ds-work-schedule__cell--staff"
                  role="rowheader"
                >
                  <span className="ds-work-schedule__text-bold">
                    {row.name}
                  </span>
                </div>
                {row.shifts.map((shift, index) => (
                  <div
                    key={`${row.id}-${index}`}
                    className={`ds-work-schedule__cell ds-work-schedule__cell--shift ds-work-schedule__cell--${
                      shift.kind
                    }${
                      shift.overrideLabel
                        ? ' ds-work-schedule__cell--has-override'
                        : ''
                    }`}
                    role="cell"
                  >
                    <span>{shift.label}</span>
                    {shift.overrideLabel ? (
                      <span className="ds-work-schedule__override">
                        {shift.overrideLabel}
                      </span>
                    ) : null}
                  </div>
                ))}
              </div>
            ))}
        </div>

        {showPagination && pagination ? (
          <Pagination
            className="ds-work-schedule__pagination"
            currentPage={pagination.currentPage}
            totalPages={totalPages}
            pageSize={pagination.pageSize}
            pageSizeOptions={
              pagination.pageSizeOptions ?? [
                ...DEFAULT_PAGINATION_PAGE_SIZE_OPTIONS,
              ]
            }
            onPageChange={pagination.onPageChange}
            onPageSizeChange={pagination.onPageSizeChange}
            darkMode={darkMode}
          />
        ) : null}
      </div>
    </div>
  )
}

export default WorkSchedule
